import { useState, type FormEvent } from 'react';
import { describeError } from '../api/errors';
import { useMessages } from '../i18n/context';
import { calendarEvents, ContentInputError, formatInputs, formatSeries, parseEvents, parseInputs, parseSeries } from './content';
import type { ResourceContentData, ResourceResponse, TimeSeriesData } from './types';

const EDITABLE = ['presentation', 'list', 'task', 'note', 'calendar', 'time-series', 'reference'];

export function ResourceEditor({ resource, busy, onSave, onCancel }: {
  resource: ResourceResponse;
  busy: boolean;
  onSave: (content: ResourceContentData) => void;
  onCancel: () => void;
}) {
  const messages = useMessages();
  const t = messages.resources;
  const [body, setBody] = useState(resource.properties['body'] ?? '');
  const [done, setDone] = useState(resource.properties['done'] === 'true');
  const [events, setEvents] = useState(() => calendarEvents(resource).map((event) => `${event.date}, ${event.title}`).join('\n'));
  const [csv, setCsv] = useState(() => formatSeries(resource.series));
  const [unit, setUnit] = useState(resource.series?.unit ?? '1');
  const [frequency, setFrequency] = useState<TimeSeriesData['frequency']>(resource.series?.frequency ?? 'quarterly');
  const [derived, setDerived] = useState(resource.calculation !== null);
  const [expression, setExpression] = useState(resource.calculation?.expression ?? '');
  const [inputs, setInputs] = useState(() => formatInputs(resource.calculation));
  const [target, setTarget] = useState(resource.targetUrn ?? '');
  const [failure, setFailure] = useState<{ cause: unknown } | null>(null);
  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (busy) return;
    setFailure(null);
    try {
      const properties: Record<string, string> = { ...resource.properties, body };
      if (resource.typeId === 'task') properties['done'] = String(done);
      if (resource.typeId === 'calendar') properties['events'] = JSON.stringify(parseEvents(events));
      onSave({
        properties,
        series: resource.typeId === 'time-series' && !derived ? parseSeries(csv, unit.trim(), frequency) : null,
        calculation: resource.typeId === 'time-series' && derived ? { expression: expression.trim(), inputs: parseInputs(inputs) } : null,
        targetUrn: resource.typeId === 'reference' ? target.trim() : null,
      });
    } catch (cause) {
      setFailure({ cause });
    }
  };
  return <form className="resource-editor card" onSubmit={submit} aria-label={t.edit}>
    <h3>{t.edit}</h3>
    {failure !== null && <p className="notice notice-error" role="alert">{failure.cause instanceof ContentInputError ? t[failure.cause.code] : describeError(failure.cause, messages.errors)}</p>}
    {!EDITABLE.includes(resource.typeId) ? <p>{t.unknownType}</p> : <>
      <label className="field">{t.body}<textarea value={body} onChange={(event) => setBody(event.target.value)} rows={3} /></label>
      {resource.typeId === 'task' && <label className="resource-task"><input type="checkbox" checked={done} onChange={(event) => setDone(event.target.checked)} />{t.completed}</label>}
      {resource.typeId === 'calendar' && <label className="field">{t.events}<textarea value={events} onChange={(event) => setEvents(event.target.value)} rows={5} /><span className="muted">{t.eventsHelp}</span></label>}
      {resource.typeId === 'reference' && <label className="field">{t.reference}<input required value={target} onChange={(event) => setTarget(event.target.value)} placeholder="urn:uuid:…" /></label>}
      {resource.typeId === 'time-series' && <>
        <label className="resource-task"><input type="checkbox" checked={derived} onChange={(event) => setDerived(event.target.checked)} />{t.derived}</label>
        {derived ? <>
          <label className="field">{t.expression}<textarea required value={expression} onChange={(event) => setExpression(event.target.value)} rows={2} /><span className="muted">{t.formulaHelp}</span></label>
          <label className="field">{t.inputs}<textarea value={inputs} onChange={(event) => setInputs(event.target.value)} rows={4} placeholder="revenue = urn:uuid:…" /><span className="muted">{t.inputsHelp}</span></label>
        </> : <>
          <div className="resource-form-row"><label className="field">{t.unit}<input required value={unit} onChange={(event) => setUnit(event.target.value)} /></label><label className="field">{t.frequency}<select value={frequency} onChange={(event) => setFrequency(event.target.value as TimeSeriesData['frequency'])}>{(['quarterly', 'monthly', 'daily', 'scalar'] as const).map((value) => <option key={value} value={value}>{t.frequencies[value]}</option>)}</select></label></div>
          <label className="field">{t.importCsv}<input type="file" accept=".csv,text/csv" onChange={(event) => {
            const file = event.target.files?.[0];
            if (file !== undefined) void file.text().then(setCsv).catch(() => setFailure({ cause: new ContentInputError('fileFailed') }));
          }} /></label>
          <label className="field">{t.data}<textarea value={csv} onChange={(event) => setCsv(event.target.value)} rows={8} spellCheck={false} /><span className="muted">{t.dataHelp}</span></label>
        </>}
      </>}
      <button type="submit" className="button-primary" disabled={busy}>{t.save}</button>
    </>}
    <button type="button" className="button-secondary" disabled={busy} onClick={onCancel}>{t.cancel}</button>
  </form>;
}
